import React, { useContext, useEffect, useState } from 'react';

import { compose } from 'redux';

import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import { CookieNames } from 'common/cookies/cookies';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import { SupportedLanguages } from 'common/i18n/supportedLanguages';
import Button from 'common/inputs/Button';
import Link from 'common/Link';
import Alert, { AlertTypes } from 'common/ui/Alert';
import SingleSelect from 'common/ui/SingleSelect';
import SwitchV2 from 'common/ui/SwitchV2';
import { H2, P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import 'css/components/subdomain/admin/_AdminLanguageSettings.scss';

type OwnProps = Record<string, never>;

type ConnectProps = {
  preferredLanguage?: string;
};

type Props = OwnProps & ConnectProps;

const AdminLanguageSettings = ({ preferredLanguage }: Props) => {
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);
  const showToast = useContext(ShowToastContext);
  const viewer = useContext(ViewerContext);
  const company = useContext(CompanyContext);

  const [editing, setEditing] = useState(false);
  const [enableTranslations, setEnableTranslations] = useState(viewer.enableTranslations);
  const [hasEdits, setHasEdits] = useState(false);
  const [locale, setLocale] = useState(preferredLanguage || 'browser');

  useEffect(() => {
    if (location.query?.success) {
      router.replace({
        pathname: location.pathname,
        query: undefined,
      });
      showToast('Language preferences updated', ToastTypes.success);
    }
  }, [location.pathname, location.query, router, showToast]);

  const onLocaleChange = (selectedOption?: { value: string }) => {
    if (!selectedOption) {
      return;
    }

    const originalValue = preferredLanguage || 'browser';
    const hasEdits =
      selectedOption.value !== originalValue || enableTranslations !== viewer.enableTranslations;

    setHasEdits(hasEdits);
    setLocale(selectedOption.value);
  };

  const onToggleTranslations = (updatedEnableTranslations: boolean) => {
    const originalPreferredLanguageValue = preferredLanguage || 'browser';
    const hasEdits =
      locale !== originalPreferredLanguageValue ||
      updatedEnableTranslations !== viewer.enableTranslations;

    setHasEdits(hasEdits);
    setEnableTranslations(updatedEnableTranslations);
  };

  const onSubmit = async () => {
    if (!hasEdits) {
      return;
    }

    setEditing(true);
    setHasEdits(false);

    const response = await AJAX.post('/api/viewer/setPreferredLanguage', {
      enableTranslations,
      language: locale === 'browser' ? null : locale,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    setEditing(false);

    if (error) {
      showToast(error.message, ToastTypes.error);
      return;
    }

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('success', 'true');
    url.search = params.toString();
    window.location.replace(url.toString());
  };

  const localeOptions = [
    {
      label: 'Use browser language',
      value: 'browser',
    },
    ...Object.entries(SupportedLanguages).map(([languageName, localeCode]) => ({
      label: languageName,
      value: localeCode,
    })),
  ];

  const selectedOption = localeOptions.find((localeOption) => {
    return locale === localeOption.value;
  });

  return (
    <div className="adminLanguageSettings">
      <Helmet title="Language Preferences | Canny" />
      <H2 className="heading" variant="bodyMd" fontWeight="bold">
        Content Translations
      </H2>
      <div className="content">
        {!company?.enableTranslations || !company?.features?.translateContent ? (
          <div className="translationsDisabled">
            <Alert headingText="Translations are disabled" type={AlertTypes.Info}>
              <Alert.Content>
                Enable auto-generated translations in your{' '}
                <Link className="link" to="/admin/settings/company/preferences">
                  general settings
                </Link>
                .
              </Alert.Content>
            </Alert>
          </div>
        ) : (
          <>
            <P>Set what language you would like to see content in.</P>
            <P className="learnMore">
              <a
                href="https://help.canny.io/en/articles/9091829-content-translations"
                target="_blank"
                rel="noreferrer">
                Learn more about translations
              </a>
            </P>

            <div className="selectContainer">
              <P>Enable automatic translations</P>
              <SwitchV2 onChange={onToggleTranslations} checked={enableTranslations} />
            </div>
            <div className="selectContainer">
              <P variant="bodyMd">Select your preferred language</P>
              <SingleSelect
                className="languageSelect"
                onChange={onLocaleChange}
                options={localeOptions}
                value={selectedOption}
                disabled={!enableTranslations}
              />
            </div>
            <Button disabled={!hasEdits} loading={editing} onTap={onSubmit} value="Save" />
          </>
        )}
      </div>
    </div>
  );
};

export default compose(
  connect(({ cookies }: { cookies: Record<string, string> }) => ({
    preferredLanguage: cookies?.[CookieNames.locale],
  })),
)(AdminLanguageSettings) as unknown as React.FC<OwnProps>;
